import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getNotifications, markAllNotificationsRead, getMyProfile, getMySchedule } from '../../lib/api';
import { syncShiftReminders, requestNotificationPermission } from '../../lib/notifications';
import { requestLocationPermission } from '../../lib/geo';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { useEstadoCrm } from '../../hooks/useEstadoCrm';
import { podeAbrirAreaCrm } from '../../lib/crm/acesso';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import ThemeToggle from '../ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard,
  Clock,
  Calendar,
  FileText,
  Bell,
  LogOut,
  User,
  MapPinned,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const navItems = [
  { path: '/colaborador', label: 'Início', icon: LayoutDashboard, exact: true },
  { path: '/colaborador/ponto', label: 'Registar Ponto', icon: Clock },
  { path: '/colaborador/ausencias', label: 'Ausências', icon: Calendar },
  { path: '/colaborador/documentos', label: 'Documentos', icon: FileText },
];
// CRM Purple House: só para membros de escritório do CRM, no browser (regras em lib/crm/acesso.js).
const ITEM_CRM = { path: '/crm/mapa', label: 'CRM', icon: MapPinned };

export default function EmployeeLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [photo, setPhoto] = useState(null);
  const { eu } = useEstadoCrm();
  const itens = podeAbrirAreaCrm(eu, { nativo: Capacitor.isNativePlatform() }) ? [...navItems, ITEM_CRM] : navItems;

  useEffect(() => {
    fetchNotifications();
    getMyProfile().then((res) => setPhoto(res.data?.photo || null)).catch(() => {});
    const syncReminders = async () => {
      try {
        const res = await getMySchedule();
        await syncShiftReminders(res.data?.work_days, res.data?.start_time);
      } catch { /* sem escala, tudo bem */ }
    };
    // Onboarding (app nativa): pedir permissões à entrada e agendar o lembrete
    (async () => {
      await requestLocationPermission();
      await requestNotificationPermission();
      await syncReminders();
    })();
    // Re-agendar quando a app volta ao primeiro plano: apanha mudanças de
    // escala sem depender de um arranque a frio.
    let handle;
    let cancelled = false;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) syncReminders();
      }).then((h) => {
        // Se o layout já desmontou entretanto, remover logo (evita listener órfão)
        if (cancelled) h.remove();
        else handle = h;
      });
    }
    return () => { cancelled = true; handle?.remove(); };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('Notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Sessão terminada');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b bg-card px-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg brand-gradient flex items-center justify-center font-heading font-bold text-white text-base">
            L
          </div>
          <span className="font-heading font-bold">Lisbonb</span>
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                Notificações
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                    Marcar todas como lidas
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ScrollArea className="h-64">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Sem notificações
                  </div>
                ) : (
                  notifications.slice(0, 10).map(notification => (
                    <DropdownMenuItem key={notification.id} className="flex flex-col items-start p-3">
                      <div className="flex items-center gap-2 w-full">
                        {!notification.read && <div className="h-2 w-2 rounded-full bg-primary" />}
                        <span className="font-medium text-sm">{notification.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center overflow-hidden ring-2 ring-primary/15">
                  {photo ? (
                    <img src={photo} alt="Foto" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/colaborador/perfil')} data-testid="profile-menu-item">
                <User className="h-4 w-4 mr-2" />
                O meu perfil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" />
                Terminar Sessão
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 pb-20">
        <Outlet />
      </main>

      {/* Bottom Navigation: os itens repartem a largura do ecrã (cada um nunca abaixo da sua
          palavra mais comprida), para nenhum ficar fora do ecrã num telemóvel estreito ou com
          letra grande — o CRM, 5.º item, ficava escondido à direita. */}
      <nav className="fixed bottom-0 left-0 right-0 border-t bg-card safe-area-inset-bottom">
        <div className="flex items-center h-16">
          {itens.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-1 px-0.5 py-1 rounded-lg transition-colors ${
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`
              }
              data-testid={`nav-${item.path.split('/').pop() || 'inicio'}`}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="text-center text-xs font-medium leading-tight">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
